/**
 * The next-work store (HOME-02): the last ranker reply (COS-25 `getNextWorkRequest`) for Home's `work` layout.
 *
 * - `load` is latest-wins: each call aborts the previous request's `AbortController`, and a reply is written
 *   only while its controller is still the current one. A slow first reply cannot overwrite a newer one.
 * - A failed read sets `loadState = 'error'` and KEEPS `result`: Home goes on showing the last
 *   list with a "could not refresh" line instead of blanking. An `AbortError` is a superseded read, not a failure.
 * - `result` is the proto-es message itself, so the store does NOT use immer (immer would draft and freeze a
 *   message carrying `$typeName` and `bigint`) and not `devtools` (its serializer cannot write a `bigint`).
 * - The list is one user's work. Not persisted; `hardLogout` calls `resetNextWorkStore`.
 */
import { create as createMessage } from '@bufbuild/protobuf';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  GetNextWorkRequestSchema,
  type GetNextWorkResponse,
} from '@/lib/protobuf/gen/cos/nextwork_pb';
import { sendRequest } from '@/lib/websocket';

export type NextWorkLoad = 'idle' | 'loading' | 'success' | 'error';

export interface NextWorkState {
  result: GetNextWorkResponse | null;
  loadState: NextWorkLoad;
  actions: { load: () => Promise<void>; reset: () => void };
}

let current: AbortController | null = null;

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export const useNextWorkStore = create<NextWorkState>()(
  subscribeWithSelector((set) => ({
    result: null,
    loadState: 'idle',
    actions: {
      load: async () => {
        current?.abort();
        const request = new AbortController();
        current = request;
        set({ loadState: 'loading' });
        try {
          const resp = await sendRequest(
            { case: 'getNextWorkRequest', value: createMessage(GetNextWorkRequestSchema, {}) },
            'getNextWorkResponse',
            { signal: request.signal },
          );
          if (current !== request) return;
          set({ result: resp, loadState: 'success' });
        } catch (err) {
          if (current !== request || isAbort(err)) return;
          set({ loadState: 'error' });
        } finally {
          if (current === request) current = null;
        }
      },
      reset: () => {
        current?.abort();
        current = null;
        set({ result: null, loadState: 'idle' });
      },
    },
  })),
);

/** Drops the held list (logout, user switch). */
export function resetNextWorkStore(): void {
  useNextWorkStore.getState().actions.reset();
}
